const API_ROOT = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002/').replace(/\/?$/, '/');

export const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

const licensingBase = `${API_ROOT}synctrack/licensing`;

export async function fetchTenants() {
  const res = await fetch(`${API_ROOT}synctrack/tenants`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`Failed to load tenants (${res.status})`);
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

export async function fetchPlans() {
  const res = await fetch(`${licensingBase}/plans`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`Failed to load plans (${res.status})`);
  return res.json();
}

/** Global dropdown catalog. Pass category e.g. SUBSCRIPTION_STATUS, or { grouped: true }. */
export async function fetchLookups(categoryOrOpts) {
  const opts =
    typeof categoryOrOpts === 'string'
      ? { category: categoryOrOpts }
      : categoryOrOpts || {};
  const qs = new URLSearchParams();
  if (opts.category) qs.set('category', opts.category);
  if (opts.grouped) qs.set('grouped', 'true');
  const url = `${licensingBase}/lookups${qs.toString() ? `?${qs}` : ''}`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`Failed to load lookups (${res.status})`);
  return res.json();
}

export async function fetchTenantSubscription(tenantId) {
  const res = await fetch(`${licensingBase}/tenants/${tenantId}/subscription`, {
    headers: getAuthHeaders(),
  });
  if (res.status === 404) return null;
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Failed to load subscription (${res.status})`);
  }
  return res.json();
}

/** All subscriptions for a tenant (history). Newest first; {@code current} marks the latest row. */
export async function fetchTenantSubscriptions(tenantId) {
  const res = await fetch(`${licensingBase}/tenants/${tenantId}/subscriptions`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Failed to load subscriptions (${res.status})`);
  }
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

export async function fetchSubscriptionById(subscriptionId) {
  const res = await fetch(`${licensingBase}/subscriptions/${subscriptionId}`, {
    headers: getAuthHeaders(),
  });
  if (res.status === 404) return null;
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Failed to load subscription (${res.status})`);
  }
  return res.json();
}

export async function provisionTenantLicense(tenantId, body) {
  const res = await fetch(`${licensingBase}/tenants/${tenantId}/provision`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    const text = await res.text();
    let msg = text;
    try {
      msg = JSON.parse(text).message || text;
    } catch {
      /* keep text */
    }
    throw new Error(msg || `Provision failed (${res.status})`);
  }
  return res.json();
}

/** Add / renew subscription (new history row). Default expires prior active terms. */
export async function addTenantSubscription(tenantId, body) {
  const res = await fetch(`${licensingBase}/tenants/${tenantId}/subscriptions`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    const text = await res.text();
    let msg = text;
    try {
      msg = JSON.parse(text).message || text;
    } catch {
      /* keep text */
    }
    throw new Error(msg || `Add subscription failed (${res.status})`);
  }
  return res.json();
}

/** One-click renew: carry forward settings, roll dates, new subscription + invoice. */
export async function renewCarryForward(tenantId, body = {}) {
  const res = await fetch(`${licensingBase}/tenants/${tenantId}/subscriptions/renew-carry-forward`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    const text = await res.text();
    let msg = text;
    try {
      msg = JSON.parse(text).message || text;
    } catch {
      /* keep text */
    }
    throw new Error(msg || `Renew failed (${res.status})`);
  }
  return res.json();
}

/** Bulk one-click renew for many tenants. */
export async function renewCarryForwardBulk(tenantIds, body = {}) {
  const res = await fetch(`${licensingBase}/subscriptions/renew-carry-forward/bulk`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ tenantIds, ...body }),
  });
  if (!res.ok) {
    const text = await res.text();
    let msg = text;
    try {
      msg = JSON.parse(text).message || text;
    } catch {
      /* keep text */
    }
    throw new Error(msg || `Bulk renew failed (${res.status})`);
  }
  return res.json();
}

/** YYYY-MM-DD from API date / string. */
export function toDateOnly(v) {
  if (v == null || v === '') return null;
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

/**
 * Next renewal term with no overlap: if current end is still in the future,
 * next start = day after end; otherwise start = today. Length follows billing cycle.
 */
export function computeRenewalPeriods({ startDate, endDate, billingCycle } = {}) {
  const currentStart = toDateOnly(startDate);
  const currentEnd = toDateOnly(endDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = currentEnd ? new Date(`${currentEnd}T00:00:00`) : null;
  const stillActive = end && end >= today;
  let nextStart;
  if (stillActive) {
    nextStart = new Date(end);
    nextStart.setDate(nextStart.getDate() + 1);
  } else {
    nextStart = new Date(today);
  }
  const cycle = String(billingCycle || 'MONTHLY').toUpperCase();
  const annual = cycle.includes('YEAR') || cycle === 'ANNUAL';
  const nextEnd = new Date(nextStart);
  if (annual) {
    nextEnd.setFullYear(nextEnd.getFullYear() + 1);
    nextEnd.setDate(nextEnd.getDate() - 1);
  } else {
    nextEnd.setMonth(nextEnd.getMonth() + 1);
    nextEnd.setDate(nextEnd.getDate() - 1);
  }
  const fmt = (d) => {
    if (!d || Number.isNaN(d.getTime())) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  return {
    currentStart,
    currentEnd,
    nextStart: fmt(nextStart),
    nextEnd: fmt(nextEnd),
    stillActive: !!stillActive,
    billingCycle: cycle,
  };
}

export async function updateSubscriptionProducts(subscriptionId, productMap) {
  const res = await fetch(`${licensingBase}/subscriptions/${subscriptionId}/products`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(productMap),
  });
  if (!res.ok) throw new Error(`Failed to update products (${res.status})`);
  return res.json();
}

export async function upsertEntitlement(subscriptionId, payload) {
  const res = await fetch(`${licensingBase}/subscriptions/${subscriptionId}/entitlements`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Failed to update entitlement (${res.status})`);
  return res.json();
}

export async function updateSubscriptionSettings(subscriptionId, payload) {
  const res = await fetch(`${licensingBase}/subscriptions/${subscriptionId}/settings`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Failed to update settings (${res.status})`);
  return res.json();
}

export async function replaceSubscriptionLlms(subscriptionId, llms) {
  const res = await fetch(`${licensingBase}/subscriptions/${subscriptionId}/llms`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(llms || []),
  });
  if (!res.ok) throw new Error(`Failed to update LLMs (${res.status})`);
  return res.json();
}

export async function fetchLicenseConnectorCatalog() {
  const res = await fetch(`${licensingBase}/catalog/connectors`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`Failed to load connector catalog (${res.status})`);
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

export async function fetchLicenseFlowCatalog() {
  const res = await fetch(`${licensingBase}/catalog/flows`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`Failed to load flow catalog (${res.status})`);
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

export async function fetchLicenseLlmCatalog() {
  const res = await fetch(`${licensingBase}/catalog/llms`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`Failed to load LLM catalog (${res.status})`);
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

export async function replaceLicensedConnectors(subscriptionId, items) {
  const res = await fetch(`${licensingBase}/subscriptions/${subscriptionId}/licensed-connectors`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(items || []),
  });
  if (!res.ok) throw new Error(`Failed to save licensed connectors (${res.status})`);
  return res.json();
}

/** Per licensed master flow UI for Sync/Hub: { [mfId]: screenUi }. */
export async function fetchTenantLicensedFlowUi(tenantId) {
  if (!tenantId) return {};
  const res = await fetch(`${licensingBase}/tenants/${tenantId}/licensed-flow-ui`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) return {};
  const data = await res.json().catch(() => ({}));
  return data?.byMfId && typeof data.byMfId === 'object' ? data.byMfId : {};
}

export async function replaceLicensedFlows(subscriptionId, items) {
  const res = await fetch(`${licensingBase}/subscriptions/${subscriptionId}/licensed-flows`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(items || []),
  });
  if (!res.ok) throw new Error(`Failed to save licensed flows (${res.status})`);
  return res.json();
}

export async function fetchAllowedLlms(tenantId) {
  const res = await fetch(`${licensingBase}/tenants/${tenantId}/allowed-llms`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error(`Failed to load allowed LLMs (${res.status})`);
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

export async function fetchLicenseAccessCheck(tenantId) {
  const res = await fetch(`${licensingBase}/tenants/${tenantId}/access-check`, {
    headers: getAuthHeaders(),
  });
  const text = await res.text();
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { allowed: false, message: text || `HTTP ${res.status}` };
  }
  if (!res.ok && data.allowed === undefined) {
    data.allowed = false;
    data.message = data.message || text || `HTTP ${res.status}`;
  }
  return data;
}

/** Statuses that freeze license content + tenant product mutations. */
export const BLOCKED_SUBSCRIPTION_STATUSES = ['EXPIRED', 'SUSPENDED', 'CANCELLED'];

/** ACTIVE / TRIAL / GRACE (and unknown/legacy) remain operable. */
export function isSubscriptionStatusOperable(status) {
  if (status == null || String(status).trim() === '') return true;
  return !BLOCKED_SUBSCRIPTION_STATUSES.includes(String(status).trim().toUpperCase());
}

export function subscriptionLockMessage(status) {
  const s = status != null ? String(status).trim().toUpperCase() : 'INACTIVE';
  return (
    `Subscription is ${s}. Products, menus, connectors, flows, and LLMs are read-only. ` +
    `This term cannot be edited in Subscriptions either — Add a new subscription to renew.`
  );
}

/** functionCode / menu name → license feature entitlement (doc Sync File flags). */
export const FUNCTION_TO_LICENSE_FEATURE = {
  'Design Share': 'FILE_SHARE',
  'File Share': 'FILE_SHARE',
  'Benchmark Hub': 'BENCHMARK',
  Projects: 'BENCHMARK',
  'Benchmark Projects': 'BENCHMARK',
  'Cost Structure': 'BENCHMARK',
  'Cost Benchmark': 'BENCHMARK',
  Benchmark: 'BENCHMARK',
  'Project Comparison': 'BENCHMARK',
  'Bid Comparison': 'BENCHMARK',
  'Knowledge Hub': 'KNOWLEDGE_BASE',
  'File Exchange': 'FILE_UPLOAD',
};

/**
 * Explicit FALSE on License blocks. Missing flag = allow (doc: only when value provided as false).
 */
export function isLicenseFeatureAllowed(functionCode) {
  try {
    if (localStorage.getItem('cdx_user') === 'true') return true;
    const raw = localStorage.getItem('licenseFeatureFlags');
    if (!raw) return true;
    const flags = JSON.parse(raw);
    const feature = FUNCTION_TO_LICENSE_FEATURE[functionCode];
    if (!feature) return true;
    if (Object.prototype.hasOwnProperty.call(flags, feature) && flags[feature] === false) {
      return false;
    }
    return true;
  } catch {
    return true;
  }
}

export async function fetchEntitlementMaster() {
  const res = await fetch(`${licensingBase}/entitlement-master`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`Failed to load entitlement master (${res.status})`);
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

export async function addEntitlementsFromMaster(subscriptionId, items) {
  const res = await fetch(`${licensingBase}/subscriptions/${subscriptionId}/entitlements/from-master`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(items || []),
  });
  if (!res.ok) {
    const text = await res.text();
    let msg = text;
    try {
      msg = JSON.parse(text).message || text;
    } catch {
      /* keep */
    }
    throw new Error(msg || `Add entitlements failed (${res.status})`);
  }
  return res.json();
}

export const PRODUCT_OPTIONS = []; // deprecated — use productsFromLookups(lookups)

/** Build product list from global.app_lookup PRODUCT rows. */
export function productsFromLookups(lookups) {
  const rows = lookups?.PRODUCT;
  if (!Array.isArray(rows) || rows.length === 0) return [];
  return rows.map((r) => ({
    code: String(r.code || '').toUpperCase(),
    name: r.label || r.code,
  })).filter((p) => p.code);
}

/**
 * FEATURE_FLAG lookup → Select options.
 * PLAN_DEFAULT maps to blank value (keep plan default).
 */
export function featureFlagOptionsFromLookups(lookups, { allowBlank = true } = {}) {
  const rows = lookups?.FEATURE_FLAG;
  if (!Array.isArray(rows) || rows.length === 0) return [];
  return rows
    .map((r) => {
      const code = String(r.code ?? '');
      if (code === 'PLAN_DEFAULT' || code === '') {
        return allowBlank ? { value: '', label: r.label || 'Plan default' } : null;
      }
      return { value: code, label: r.label || code };
    })
    .filter(Boolean);
}

/** Map lookup billing code to legacy register API monthly/yearly. */
export function billingCycleToRegisterSubscription(code) {
  const c = String(code || '').trim().toUpperCase();
  if (c.startsWith('YEAR') || c === 'ANNUAL') return 'yearly';
  return 'monthly';
}

/** Map legacy monthly/yearly → BILLING_CYCLE code. */
export function registerSubscriptionToBillingCycle(sub) {
  const c = String(sub || '').trim().toLowerCase();
  if (c.startsWith('year') || c === 'annual') return 'ANNUAL';
  return 'MONTHLY';
}

/** Optional register/license extras — blank means keep plan default. */
export const emptyOptionalExtras = () => ({
  gracePeriodDays: '',
  licenseStartDate: '',
  licenseEndDate: '',
  maxUsers: '',
  productionEnabled: '',
  sandboxEnabled: '',
  ssoEnabled: '',
  mfaEnabled: '',
  auditLogsEnabled: '',
  apiAccessEnabled: '',
  fileShareEnabled: '',
  fileUploadEnabled: '',
  benchmarkEnabled: '',
  knowledgeBaseEnabled: '',
  maxAiAgents: '',
  maxAgentTeams: '',
  maxAiTokensMonth: '',
  maxConnectors: '',
  maxFlows: '',
  storageGb: '',
  ragStorageGb: '',
  maxApiCallsMonth: '',
  maxMcpServers: '',
  maxTenantAdmins: '',
});

const TRI_BOOL_KEYS = [
  'productionEnabled',
  'sandboxEnabled',
  'ssoEnabled',
  'mfaEnabled',
  'auditLogsEnabled',
  'apiAccessEnabled',
  'fileShareEnabled',
  'fileUploadEnabled',
  'benchmarkEnabled',
  'knowledgeBaseEnabled',
];

const INT_KEYS = [
  'gracePeriodDays',
  'maxUsers',
  'maxAiAgents',
  'maxAgentTeams',
  'maxConnectors',
  'maxFlows',
  'storageGb',
  'ragStorageGb',
  'maxMcpServers',
  'maxTenantAdmins',
];

const LONG_KEYS = ['maxApiCallsMonth', 'maxAiTokensMonth'];

const STRING_KEYS = ['licenseStartDate', 'licenseEndDate'];

/** Build API fields from optional extras form; omits blanks. */
export function buildOptionalExtrasPayload(extras) {
  if (!extras) return {};
  const out = {};
  STRING_KEYS.forEach((k) => {
    const v = extras[k];
    if (v != null && String(v).trim() !== '') out[k] = String(v).trim();
  });
  INT_KEYS.forEach((k) => {
    const v = extras[k];
    if (v !== '' && v != null && !Number.isNaN(Number(v))) out[k] = Number(v);
  });
  LONG_KEYS.forEach((k) => {
    const v = extras[k];
    if (v !== '' && v != null && !Number.isNaN(Number(v))) out[k] = Number(v);
  });
  TRI_BOOL_KEYS.forEach((k) => {
    if (extras[k] === 'true') out[k] = true;
    else if (extras[k] === 'false') out[k] = false;
  });
  return out;
}

/** Map subscription entitlements → optional extras form values. */
export function extrasFromEntitlements(entitlements) {
  const map = {};
  (entitlements || []).forEach((e) => {
    map[e.code] = e.value;
  });
  const tri = (code) => {
    const v = map[code];
    if (v == null || v === '') return '';
    const s = String(v).toUpperCase();
    if (s === 'TRUE' || s === '1') return 'true';
    if (s === 'FALSE' || s === '0') return 'false';
    return '';
  };
  const num = (code) => (map[code] != null && map[code] !== '' ? String(map[code]) : '');
  return {
    ...emptyOptionalExtras(),
    maxUsers: num('MAX_USERS'),
    productionEnabled: tri('PRODUCTION'),
    sandboxEnabled: tri('SANDBOX'),
    ssoEnabled: tri('SSO'),
    mfaEnabled: tri('MFA'),
    auditLogsEnabled: tri('AUDIT_LOGS'),
    apiAccessEnabled: tri('API_ACCESS'),
    fileShareEnabled: tri('FILE_SHARE'),
    fileUploadEnabled: tri('FILE_UPLOAD'),
    benchmarkEnabled: tri('BENCHMARK'),
    knowledgeBaseEnabled: tri('KNOWLEDGE_BASE'),
    maxAiAgents: num('MAX_AI_AGENTS'),
    maxAgentTeams: num('MAX_AGENT_TEAMS'),
    maxAiTokensMonth: num('MAX_AI_TOKENS_MONTH'),
    maxConnectors: num('MAX_CONNECTORS'),
    maxFlows: num('MAX_FLOWS'),
    storageGb: num('STORAGE_GB'),
    ragStorageGb: num('RAG_STORAGE_GB'),
    maxApiCallsMonth: num('MAX_API_CALLS_MONTH'),
    maxMcpServers: num('MAX_MCP_SERVERS'),
    maxTenantAdmins: num('MAX_TENANT_ADMINS'),
  };
}

/** Entitlement upsert specs for license configuration form (doc §4 fields). */
export const OPTIONAL_ENTITLEMENT_SPECS = [
  { code: 'MAX_USERS', productCode: 'PLATFORM', type: 'NUMBER', unit: 'USERS', formKey: 'maxUsers' },
  { code: 'MAX_TENANT_ADMINS', productCode: 'PLATFORM', type: 'NUMBER', unit: 'ADMINS', formKey: 'maxTenantAdmins' },
  { code: 'PRODUCTION', productCode: 'PLATFORM', type: 'BOOLEAN', formKey: 'productionEnabled' },
  { code: 'SANDBOX', productCode: 'PLATFORM', type: 'BOOLEAN', formKey: 'sandboxEnabled' },
  { code: 'SSO', productCode: 'PLATFORM', type: 'BOOLEAN', formKey: 'ssoEnabled' },
  { code: 'MFA', productCode: 'PLATFORM', type: 'BOOLEAN', formKey: 'mfaEnabled' },
  { code: 'AUDIT_LOGS', productCode: 'PLATFORM', type: 'BOOLEAN', formKey: 'auditLogsEnabled' },
  { code: 'MAX_CONNECTORS', productCode: 'HUB', type: 'NUMBER', unit: 'CONNECTORS', formKey: 'maxConnectors' },
  { code: 'MAX_FLOWS', productCode: 'HUB', type: 'NUMBER', unit: 'FLOWS', formKey: 'maxFlows' },
  { code: 'MAX_API_CALLS_MONTH', productCode: 'HUB', type: 'NUMBER', unit: 'CALLS', formKey: 'maxApiCallsMonth' },
  { code: 'API_ACCESS', productCode: 'HUB', type: 'BOOLEAN', formKey: 'apiAccessEnabled' },
  { code: 'STORAGE_GB', productCode: 'SYNC_FILE', type: 'NUMBER', unit: 'GB', formKey: 'storageGb' },
  { code: 'RAG_STORAGE_GB', productCode: 'SYNC_FILE', type: 'NUMBER', unit: 'GB', formKey: 'ragStorageGb' },
  { code: 'FILE_SHARE', productCode: 'SYNC_FILE', type: 'BOOLEAN', formKey: 'fileShareEnabled' },
  { code: 'FILE_UPLOAD', productCode: 'SYNC_FILE', type: 'BOOLEAN', formKey: 'fileUploadEnabled' },
  { code: 'BENCHMARK', productCode: 'SYNC_FILE', type: 'BOOLEAN', formKey: 'benchmarkEnabled' },
  { code: 'MAX_AI_AGENTS', productCode: 'AGENTIC_AI', type: 'NUMBER', unit: 'AGENTS', formKey: 'maxAiAgents' },
  { code: 'MAX_AGENT_TEAMS', productCode: 'AGENTIC_AI', type: 'NUMBER', unit: 'TEAMS', formKey: 'maxAgentTeams' },
  { code: 'MAX_AI_TOKENS_MONTH', productCode: 'AGENTIC_AI', type: 'NUMBER', unit: 'TOKENS', formKey: 'maxAiTokensMonth' },
  { code: 'MAX_MCP_SERVERS', productCode: 'AGENTIC_AI', type: 'NUMBER', unit: 'SERVERS', formKey: 'maxMcpServers' },
  { code: 'KNOWLEDGE_BASE', productCode: 'AGENTIC_AI', type: 'BOOLEAN', formKey: 'knowledgeBaseEnabled' },
];

/** Codes edited by the license configuration form (hide from advanced raw table). */
export const FORM_MANAGED_ENTITLEMENT_CODES = new Set(
  OPTIONAL_ENTITLEMENT_SPECS.map((s) => s.code)
);

export const USAGE_TYPE_LABELS = {
  USERS: 'Current users',
  AI_AGENTS: 'Active AI agents',
  CONNECTORS: 'Active connectors',
  FLOWS: 'Active flows',
  STORAGE_GB: 'Storage used',
  RAG_STORAGE_GB: 'RAG storage used',
  API_CALLS: 'API calls used',
  AI_TOKENS: 'AI tokens used',
  MCP_SERVERS: 'MCP servers',
  AGENT_TEAMS: 'Agent teams',
};

/** Parse MAX_USERS entitlement + USERS usage from a tenant subscription DTO. */
export function getUserSeatLimit(subscription, fallbackUsed = null) {
  if (!subscription) {
    return { maxUsers: null, usedUsers: null, remaining: null, atLimit: false };
  }
  const ent = (subscription.entitlements || []).find(
    (e) => String(e.code || '').toUpperCase() === 'MAX_USERS'
  );
  let maxUsers = null;
  if (ent?.value != null && String(ent.value).trim() !== '') {
    const n = Number(String(ent.value).replace(/,/g, '').trim());
    if (Number.isFinite(n)) maxUsers = n;
  }
  const usage = (subscription.usage || []).find(
    (u) => String(u.usageType || '').toUpperCase() === 'USERS'
  );
  let usedFromUsage = null;
  if (usage?.usedQuantity != null) {
    const n = Number(usage.usedQuantity);
    if (Number.isFinite(n)) usedFromUsage = n;
  }
  const fallback =
    fallbackUsed != null && Number.isFinite(Number(fallbackUsed)) ? Number(fallbackUsed) : null;
  // Prefer live grid count when usage is stale (e.g. admin created at register, usage still 0)
  let usedUsers = usedFromUsage;
  if (fallback != null) {
    usedUsers = usedFromUsage == null ? fallback : Math.max(usedFromUsage, fallback);
  }
  const remaining =
    maxUsers != null && usedUsers != null ? Math.max(0, maxUsers - usedUsers) : null;
  const atLimit = maxUsers != null && usedUsers != null && usedUsers >= maxUsers;
  return { maxUsers, usedUsers, remaining, atLimit };
}
